import { useId, useState, useEffect, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { formatThaiDate, formatWeekRangeFromDate } from "../utils/date";
import "vanillajs-datepicker/css/datepicker.css";
import "../../../static/css/datepicker.css";
import Icon from "./Icon.jsx";
import MenuList from "./MenuList.jsx";
import handleClickOutsideElement from '../utils/handleClickOutsideElement';
import DatepickerPopover from "./DatepickerPopover.jsx";
import DateRangePickerPopover from "./DateRangePickerPopover.jsx";

/**
 * InputField component
 *
 * Renders a styled input field group with label, helper text, and
 * support for multiple input types (text, textarea, search, select, calendar, password).
 *
 * @param {object} props
 * @param {string} props.name
 *   The name attribute for the input field. Also used as the base id.
 *
 * @param {string} [props.label]
 *   Label text displayed above the input.
 *
 * @param {string} [props.placeholder]
 *   Placeholder text for the input element.
 *
 * @param {string|number} [props.value]
 *   Current value of the input. For controlled usage, combine with onChange.
 *
 * @param {string} [props.type="text"]
 *   Visual / behavior type of the field.
 *   Allowed: "text", "textarea", "search", "select", "calendar", "password".
 *
 * @param {string} [props.inputType="text"]
 *   Native input type for standard text inputs (e.g. "text", "email", "tel").
 *   Ignored for "textarea", "select", "calendar".
 *
 * @param {number} [props.textareaRows=4]
 *   Number of rows to render when `type="textarea"`.
 *
 * @param {number} [props.minTextareaRows]
 *   Minimum number of rows to render when `type="textarea"`. Defaults to textareaRows.
 *
 * @param {number} [props.maxTextareaRows=10]
 *   Maximum number of rows to render when `type="textarea"`.
 *
 * @param {boolean} [props.isRequired=false]
 *   Marks the field as required. Adds `required` attribute and displays *.
 *
 * @param {boolean} [props.isOptional=false]
 *   Marks the field as optional. Shows "-ไม่บังคับ" next to label.
 * 
 * @param {boolean} [props.hasLabelDescription=false]
 *  If true, displays the labelDescription text next to the label.
 * 
 * @param {string} [props.labelDescription]
 *  Text to display next to the label when hasLabelDescription is true.
 *
 * @param {string} [props.infoText]
 *   Informational helper text displayed below the input.
 *
 * @param {string} [props.successText]
 *   Success helper text displayed below the input.
 *
 * @param {string} [props.errorText]
 *   Error message displayed below the input.
 *   Also toggles error styling on the input element.
 * 
 * @param {boolean} [props.allowFreeText=true]
 *   For `type="search"`: if false, the input is cleared on blur if the value is not from the suggestions.
 * 
 * @param {string} [props.icon]
 *   Icon name to display alongside the label.
 *
 * @param {boolean} [props.showIcon=true]
 *   Controls whether the prefix icon (if any) is shown.
 *
 * @param {boolean} [props.disabled=false]
 *   Disables the input element (and select/calendar if applicable).
 *
 * @param {Array<[string, string, string?]>} [props.choices]
 *   For `type="select"`: array of [value, label, description?] option tuples.
 *
 * @param {string} [props.minDate]
 *   For `type="calendar"`: minimum date in "YYYY-MM-DD" format.
 *
 * @param {string} [props.maxDate]
 *   For `type="calendar"`: maximum date in "YYYY-MM-DD" format.
 * 
 * @param {boolean} [props.isWeek=false]
 *   For `type="calendar"`: if true, enables week selection mode from a single date, starting from Sunday.
 *
 * @param {boolean} [props.allowYearSearch=false]
 *   For `type="calendar"`: if true, enables searching years in the datepicker popover.
 *
 * @param {string} [props.dropdownExtraClasses]
 *   Extra CSS classes applied to the `<select>` element container (if needed).
 *
 * @param {string} [props.className]
 *   Extra CSS classes appended to the main input element (input/textarea/select).
 *
 * @param {boolean} [props.showBorder=true]
 *   Controls whether the default input border should be rendered.
 *
 * @param {function} [props.onChange]
 *   Change handler for the underlying input/select/textarea.
 *
 * @param {function} [props.onBlur]
 *   Blur handler.
 *
 * @param {function} [props.onFocus]
 *   Focus handler.
 *
 * @param {object|function} [props.inputRef]
 *   Ref forwarded to the underlying input or textarea element when supported.
 *
 * @param {function} [props.maxLength]
 *   Maximum number of characters the user is allowed to enter.
 * 
 * @param {Array<[any, string]>} [props.suggestions]
 *   For `type="search"`: an array of `[value, label]` tuples for static search suggestions.
 *   The `value` entry is forwarded as `event.target.selectedSuggestionValue` when a suggestion is picked.
 *
 * @param {function} [props.onSearch]
 *   For `type="search"`: a function to fetch suggestions dynamically.
 *   It receives the current query and should return a promise resolving to an array of `[value, label]` tuples.
 *   The same selection behavior as `suggestions` applies (`selectedSuggestionValue` will mirror the tuple's first entry).
 * 
 * @param {boolean} [props.showSuggestionsOnFocus=true]
 *   For `type="search"`: if true, shows suggestions when the input is focused, even if it's empty.
 *   Works with static `suggestions` prop. `onSearch` will be called with an empty query.
 * 
 * @param {number} [props.debounceTimeout=200]
 *   For `type="search"` with `onSearch`: debounce timeout in milliseconds.
 * 
 * @param {number} [props.minCharsForSuggestions=1]
 *   For `type="search"`: minimum number of characters to trigger suggestions.
 *
 * @param {boolean} [props.preventPopoverCloseOnClick=false]
 *   For `type="select"`/`type="search"`: stop mousedown bubbling from the popover,
 *   so parent popovers using click-outside handlers do not close.
 * 
 * @param {boolean} [props.allowClear=true]
 *   Shows a clear-value button for supported input variants.
 * 
 * @param {function} [props.onScrollEnd]
 *   For `type="select"`: called when the dropdown is scrolled near the bottom.
 *   Use to trigger loading the next page of choices.
  * @param {boolean} [props.hasMoreChoices=false]
  *   For `type="select"`: whether more choices can be loaded. Controls the scroll trigger.
  * @param {boolean} [props.isLoadingMoreChoices=false]
  *   For `type="select"`: shows a loading indicator at the bottom of the dropdown.
  * @param {string} [props.inputMode]
  *   HTML `inputmode` attribute forwarded to the underlying input element.
  * @param {string} [props.disabledVariant]
  *   Variant used when input is disabled (e.g. "readable").
  * @param {boolean} [props.alwaysShowPlaceholder=false]
  *   Keeps the placeholder visible even when disabled and empty, opting out
  *   of the default hint-hiding behavior for disabled empty fields.
  * @param {object} [props.rest]
  *   Additional HTML attributes forwarded to the underlying input element
  *   (React equivalent of `extra_attrs`).
  */
export default function InputField({
    name,
    label = "",
    placeholder = "",
    value = "",
    type = "text",
    inputType = "text",
    textareaRows = 4,
    minTextareaRows,
    maxTextareaRows = 10,
    isRequired = false,
    isOptional = false,
    labelDescription = "",
    infoText = "",
    successText = "",
    errorText = "",
    isError = false,
    icon,
    showIcon = true,
    disabled = false,
    choices = [],
    startDate,
    endDate,
    minDate,
    maxDate,
    isWeek = false,
    allowYearSearch = false,
    dropdownExtraClasses = "",
    className = "",
    showBorder = true,
    onChange,
    onBlur,
    onFocus,
    inputRef,
    maxLength,
    suggestions = [],
    onSearch,
    showSuggestionsOnFocus = true,
    debounceTimeout = 200,
    minCharsForSuggestions = 1,
    allowFreeText = true,
    preventPopoverCloseOnClick = false,
    allowClear = true,
    popoverPlacement = "auto",
    calendarEra = "BE",
    showCounter = false,
    onScrollEnd,
    hasMoreChoices = false,
    isLoadingMoreChoices = false,
    disabledVariant,
    alwaysShowPlaceholder = false,
    ...rest
}) {
    const reactId = useId();
    const inputId = name || reactId;
    const [showPassword, setShowPassword] = useState(false);
    const [showSelect, setShowSelect] = useState(false);
    const [showCalendarPopover, setShowCalendarPopover] = useState(false);
    const [showDateRangePopover, setShowDateRangePopover] = useState(false);
    const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [hasMoreSuggestions, setHasMoreSuggestions] = useState(false);
    const [isLoadingMoreSuggestions, setIsLoadingMoreSuggestions] = useState(false);
    const textareaRef = useRef(null);
    const [isInputFocused, setIsInputFocused] = useState(false);

    const hasError = isError || Boolean(errorText);
    const hasSuccess = Boolean(successText);

    const isEmptyValue = type === "date-range"
        ? !(startDate && endDate)
        : Array.isArray(value)
            ? value.length === 0
            : value === null || value === undefined || value === "";

    // A disabled field with nothing typed shouldn't show hint text — it reads
    // like an actionable prompt even though the field can't be interacted with.
    // alwaysShowPlaceholder opts a field out, e.g. detail fields disabled by a
    // sibling "no" radio (drug allergy/smoking/drinking) that should keep
    // showing their hint even while disabled.
    const effectivePlaceholder = disabled && isEmptyValue && !alwaysShowPlaceholder ? "" : placeholder;

    const inputClasses = buildInputClassName({
        base: "input-field",
        hasError,
        extra: className,
        type,
        icon,
        showBorder,
        showIcon,
        disabledVariant,
    });

    const wrapperClasses = "input-field-group";

    const handleChange = (event) => {
        if (typeof onChange === "function") {
            onChange(event);
        }
    };

    const handleFocusEvent = (event) => {
        setIsInputFocused(true);
        if (typeof onFocus === "function") {
            onFocus(event);
        }
    };

    const handleBlurEvent = (event) => {
        setIsInputFocused(false);
        if (typeof onBlur === "function") {
            onBlur(event);
        }
    };

    const commonProps = {
        id: inputId,
        name,
        placeholder: effectivePlaceholder,
        disabled,
        required: isRequired,
        onChange: handleChange,
        onBlur: handleBlurEvent,
        onFocus: handleFocusEvent,
        maxLength,
        ...rest,
    };

    useLayoutEffect(() => {
        if (type !== "textarea") {
            return;
        }
        const element = textareaRef.current;
        if (!element) {
            return;
        }

        const effectiveMinRows = minTextareaRows ?? textareaRows;
        const effectiveMaxRows = maxTextareaRows;

        // Save current scroll position
        const scrollContainer = element.closest(".custom-scrollbar, .overflow-y-auto, .overflow-auto") || document.documentElement;
        const initialScrollTop = scrollContainer === document.documentElement ? window.scrollY : scrollContainer.scrollTop;

        // Briefly lock heights to prevent jump
        const parent = element.parentElement;
        const originalParentHeight = parent ? parent.offsetHeight : 0;
        if (parent) parent.style.minHeight = `${originalParentHeight}px`;

        // Calculate line height and dimensions in pixels
        const style = window.getComputedStyle(element);
        const fontSize = parseFloat(style.fontSize);
        // Default to a 1.5 multiplier if line-height is 'normal' (standard for the project's typography)
        const lineHeightValue = style.lineHeight === "normal" ? fontSize * 1.5 : parseFloat(style.lineHeight);
        const pt = parseFloat(style.paddingTop);
        const pb = parseFloat(style.paddingBottom);
        const bt = parseFloat(style.borderTopWidth);
        const bb = parseFloat(style.borderBottomWidth);

        const padding = pt + pb;
        const border = bt + bb;

        const minHeight = effectiveMinRows * lineHeightValue + padding + border;
        const maxHeight = effectiveMaxRows * lineHeightValue + padding + border;

        // Apply constraints to prevent manual resize from exceeding bounds
        element.style.minHeight = `${minHeight}px`;
        element.style.maxHeight = `${maxHeight}px`;

        // Reset only the computed height before measuring. Changing `rows` here
        // causes the textarea to briefly collapse on every keystroke, which can
        // make the surrounding modal jump even though this runs in a layout effect.
        element.style.height = "auto";
        const contentHeight = element.scrollHeight + border;

        // Apply automatic height constrained by the same bounds
        const finalHeight = Math.max(minHeight, Math.min(contentHeight, maxHeight));
        element.style.height = `${finalHeight}px`;

        // Manage scrollbar visibility
        if (contentHeight > maxHeight + 1) {
            element.style.overflowY = "auto";
        } else {
            element.style.overflowY = "hidden";
        }

        // Unlock parent height
        if (parent) parent.style.minHeight = "";

        // Restore scroll position
        if (scrollContainer === document.documentElement) {
            window.scrollTo(window.scrollX, initialScrollTop);
        } else {
            scrollContainer.scrollTop = initialScrollTop;
        }
    }, [type, value, textareaRows, minTextareaRows, maxTextareaRows]);

    return (
        <div className={wrapperClasses}>
            {label && (
                <label htmlFor={inputId} className="label">
                    {label}
                    {isRequired && (
                        <span className="text-main-typography-accented-error font-normal">
                            *
                        </span>
                    )}
                    {isOptional && (
                        <span className="text-main-typography-system-caption font-normal">
                            -ไม่บังคับ
                        </span>
                    )}
                    {labelDescription && (
                        <span className="text-main-typography-system-caption font-normal">
                            {labelDescription}
                        </span>
                    )}
                </label>
            )}

            <div className="relative">
                {renderInputByType({
                    type,
                    value,
                    inputType,
                    disabled,
                    placeholder: effectivePlaceholder,
                    icon,
                    showIcon,
                    inputClasses,
                    commonProps,
                    choices,
                    minDate,
                    maxDate,
                    isWeek,
                    allowYearSearch,
                    dropdownExtraClasses,
                    showPassword,
                    setShowPassword,
                    showSelect,
                    setShowSelect,
                    showCalendarPopover,
                    setShowCalendarPopover,
                    showDateRangePopover,
                    setShowDateRangePopover,
                    startDate,
                    endDate,
                    textareaRows,
                    minTextareaRows,
                    maxTextareaRows,
                    textareaRef,
                    inputRef,
                    maxLength,
                    suggestions,
                    onSearch,
                    showSuggestionsOnFocus,
                    debounceTimeout,
                    minCharsForSuggestions,
                    showSearchSuggestions,
                    setShowSearchSuggestions,
                    searchSuggestions,
                    setSearchSuggestions,
                    hasMoreSuggestions,
                    setHasMoreSuggestions,
                    isLoadingMoreSuggestions,
                    setIsLoadingMoreSuggestions,
                    allowFreeText,
                    preventPopoverCloseOnClick,
                    allowClear,
                    isInputFocused,
                    popoverPlacement,
                    calendarEra,
                    onScrollEnd,
                    hasMoreChoices,
                    isLoadingMoreChoices,
                })}
            </div>

            {showCounter && maxLength && (
                <div className="flex justify-end">
                    <span className="text-helper text-helper-info">
                        {String(value || "").length}/{maxLength}
                    </span>
                </div>
            )}

            {/* Error text */}
            {errorText && (
                <div
                    id={`${inputId}_error_text`}
                    className={`text-helper text-helper-error ${!hasError ? "hidden" : ""
                        }`}
                >
                    <Icon name="x-circle" size="sm" />
                    <p className="px-2">{errorText}</p>
                </div>
            )}

            {/* Success text */}
            <div
                id={`${inputId}_success_text`}
                className={`text-helper text-helper-success ${!hasSuccess ? "hidden" : ""
                    }`}
            >
                <Icon name="check-circle" size="sm" />
                <p className="px-2">{successText}</p>
            </div>

            {/* Info text */}
            {infoText && (
                <div className="text-helper text-helper-info">
                    <Icon name="information-circle" size="sm" />
                    <p className="px-2">{infoText}</p>
                </div>
            )}
        </div>
    );
}

function FloatingPortal({
    anchorRef,
    isOpen,
    popoverRef,
    className = "",
    matchWidth = true,
    offset = 0,
    zIndex = 9999,
    onMouseDown,
    children,
    placement = "auto",
}) {
    const [style, setStyle] = useState(null);

    useLayoutEffect(() => {
        if (!isOpen) {
            setStyle(null);
            return;
        }

        const updatePosition = () => {
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

            if (placement === "center") {
                const nextStyle = {
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex,
                    maxWidth: "90vw",
                    maxHeight: "90vh",
                };
                if (matchWidth && anchorRef.current) {
                    nextStyle.width = `${anchorRef.current.getBoundingClientRect().width}px`;
                }
                setStyle(nextStyle);
                return;
            }

            if (!anchorRef.current) {
                return;
            }
            const rect = anchorRef.current.getBoundingClientRect();
            let left = rect.left;
            let topStr = `${rect.bottom + offset}px`;
            let bottomStr = "auto";

            if (popoverRef.current) {
                // Set width before measuring to ensure position calculation is based on the final layout
                if (matchWidth && rect && typeof rect.width === 'number') {
                    popoverRef.current.style.width = `${rect.width}px`;
                }

                const popoverWidth = popoverRef.current.offsetWidth;
                const popoverHeight = popoverRef.current.offsetHeight;

                if (left + popoverWidth > viewportWidth) {
                    left = Math.max(10, viewportWidth - (popoverWidth + 10));
                }

                // Decide placement
                let shouldPlaceTop = placement === "top";
                if (placement === "auto") {
                    const spaceBelow = viewportHeight - rect.bottom - offset;
                    const spaceAbove = rect.top - offset;
                    const buffer = 8; // Buffer to handle zoom and sub-pixel rounding

                    // Only flip if there's not enough space below AND there's more space available above
                    if (spaceBelow < popoverHeight + buffer && spaceAbove > spaceBelow) {
                        shouldPlaceTop = true;
                    }
                }

                if (shouldPlaceTop) {
                    topStr = "auto";
                    bottomStr = `${viewportHeight - rect.top + offset}px`;
                }
            }

            const nextStyle = {
                position: "fixed",
                top: topStr,
                bottom: bottomStr,
                left: left,
                zIndex,
            };

            if (matchWidth) {
                nextStyle.width = `${rect.width}px`;
            }

            setStyle((prev) => {
                if (
                    prev &&
                    prev.top === nextStyle.top &&
                    prev.bottom === nextStyle.bottom &&
                    prev.left === nextStyle.left &&
                    prev.width === nextStyle.width
                ) {
                    return prev;
                }
                return nextStyle;
            });
        };

        updatePosition();
        // Listen for resize and scroll to keep positioned
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);

        // Also update if the popover itself changes size (e.g. content loads)
        let resizeObserver = null;
        if (popoverRef.current) {
            resizeObserver = new ResizeObserver(updatePosition);
            resizeObserver.observe(popoverRef.current);
        }

        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
            resizeObserver?.disconnect();
        };
    }, [anchorRef, isOpen, matchWidth, offset, zIndex, placement]);

    if (!isOpen) {
        return null;
    }

    const mergedStyle = style || {
        position: "fixed",
        top: "-9999px",
        left: "-9999px",
        visibility: "hidden"
    };

    return createPortal(
        <>
            {isOpen && placement === "center" && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        zIndex: zIndex - 1,
                    }}
                />
            )}
            <div
                ref={popoverRef}
                className={className}
                style={mergedStyle}
                onMouseDown={onMouseDown}
            >
                {children}
            </div>
        </>,
        document.body
    );
}

/**
 * Build base input CSS classes from state and type.
 *
 * @param {object} options
 * @param {string} options.base
 * @param {boolean} options.hasError
 * @param {string} [options.extra]
 * @param {string} [options.type]
 * @returns {string}
 */
function buildInputClassName({
    base,
    hasError,
    extra = "",
    type = "text",
    icon,
    showBorder = true,
    showIcon = true,
    disabledVariant,
}) {
    const classes = [base];

    if (hasError) {
        classes.push("input-field-error");
    }

    if (!showBorder) {
        classes.push("input-field-borderless");
    }

    if (disabledVariant === "readable") {
        classes.push("input-field-disabled-readable");
    }

    if ((type === "search" && showIcon) || (icon && showIcon) || (type === "calendar" && showIcon) || (type === "date-range" && showIcon)) {
        classes.push("ps-7");
    }

    return [...classes, extra].filter(Boolean).join(" ");
}

/**
 * Render the correct input element based on `type`.
 * Mirrors the behavior of the original Django template:
 * - textarea
 * - select
 * - calendar
 * - search
 * - password
 * - default text input
 */
function renderInputByType(options) {
    const {
        type,
        value,
        inputType,
        disabled,
        placeholder,
        icon,
        showIcon,
        inputClasses,
        commonProps,
        choices,
        minDate,
        maxDate,
        isWeek,
        allowYearSearch,
        dropdownExtraClasses,
        showPassword,
        setShowPassword,
        showSelect,
        setShowSelect,
        showCalendarPopover,
        setShowCalendarPopover,
        showDateRangePopover,
        setShowDateRangePopover,
        startDate,
        endDate,
        textareaRows,
        minTextareaRows,
        maxTextareaRows,
        textareaRef,
        inputRef,
        maxLength,
        suggestions,
        onSearch,
        showSuggestionsOnFocus,
        debounceTimeout,
        minCharsForSuggestions,
        showSearchSuggestions,
        setShowSearchSuggestions,
        searchSuggestions,
        setSearchSuggestions,
        hasMoreSuggestions,
        setHasMoreSuggestions,
        isLoadingMoreSuggestions,
        setIsLoadingMoreSuggestions,
        allowFreeText,
        preventPopoverCloseOnClick,
        allowClear,
        isInputFocused,
        popoverPlacement,
        calendarEra,
        onScrollEnd,
        hasMoreChoices,
        isLoadingMoreChoices,
    } = options;
    const emitSyntheticChange = (nextValue, extra = {}) => {
        if (typeof commonProps.onChange === "function") {
            commonProps.onChange({
                target: {
                    name: commonProps.name,
                    value: nextValue,
                    ...extra,
                },
            });
        }
    };
    const buildClearButton = ({ onClear }) => (
        <button
            type="button"
            tabIndex={-1}
            className="absolute inset-y-0 end-0 h-[24px] w-[24px] flex me-3 mt-3 mb-3"
            onMouseDown={(event) => event.preventDefault()}
            onClick={onClear}
        >
            <Icon
                name="x"
                size="md"
                className="text-creation-neutral-container-on-surface-default"
            />
        </button>
    );

    const handleSelect = (optionValue) => {
        const syntheticEvent = {
            target: {
                name: commonProps.name,
                value: optionValue,
            },
        };
        if (typeof commonProps.onChange === "function") {
            commonProps.onChange(syntheticEvent);
        }
        setShowSelect(false);
    };

    function buildIcon() {
        return (
            <div className="absolute inset-y-0 start-0 flex ms-3 mt-3 mb-3 pointer-events-none">
                <Icon
                    name={icon}
                    size="md"
                    className={
                        disabled
                            ? "text-creation-neutral-container-on-surface-disabled"
                            : "text-creation-neutral-container-on-surface-default"
                    }
                />
            </div>
        );
    }

    function buildChevronIcon({ isExpanded, isDisabled }) {
        return (
            <div className="absolute inset-y-0 end-0 flex items-center me-3 pointer-events-none">
                <Icon
                    name={isExpanded ? "chevron-up" : "chevron-down"}
                    size="md"
                    className={
                        isDisabled
                            ? "text-creation-neutral-container-on-surface-disabled"
                            : "text-creation-neutral-container-on-surface-default"
                    }
                />
            </div>
        );
    }

    if (type === "textarea") {
        const showTextareaClear =
            allowClear &&
            !disabled &&
            isInputFocused &&
            value !== "" &&
            value !== null &&
            value !== undefined;
        return (
            <div className="relative">
                {icon && showIcon && buildIcon()}
                <textarea
                    {...commonProps}
                    rows={textareaRows}
                    className={`${inputClasses} ${showTextareaClear ? "pe-7" : ""}`}
                    value={value ?? ""}
                    ref={inputRef ?? textareaRef}
                />
                {showTextareaClear &&
                    buildClearButton({
                        onClear: () => emitSyntheticChange(""),
                    })}
            </div>
        );
    }

    if (type === "select") {
        const shouldDisabled = choices.length === 0 || disabled;
        const selectedTuple = choices.find(([v]) => String(v) === String(value));
        const selectedLabel = selectedTuple ? selectedTuple[1] : "";
        const displayLabel = selectedLabel || placeholder || "";

        const choicePopupRef = useRef(null);
        const menuListContainerRef = useRef(null);
        const selectAnchorRef = useRef(null);
        const selectPopoverRef = useRef(null);
        handleClickOutsideElement([choicePopupRef, selectPopoverRef], () => setShowSelect(false));

        const handleSelectScroll = (e) => {
            const el = e.currentTarget;
            const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (distanceToBottom < 80 && hasMoreChoices && !isLoadingMoreChoices) {
                onScrollEnd?.();
            }
        };

        useEffect(() => {
            if (showSelect && menuListContainerRef.current && value) {
                // The selector is looking for a child of menuListContainerRef
                const selectedItem = menuListContainerRef.current.querySelector(`[data-value="${value}"]`);
                if (selectedItem) {
                    selectedItem.scrollIntoView({ block: 'nearest' });
                }
            }
        }, [showSelect, value]);

        const handlePopoverMouseDown = preventPopoverCloseOnClick
            ? (event) => event.stopPropagation()
            : undefined;

        return (
            <div ref={choicePopupRef} className={`relative ${dropdownExtraClasses}`}>
                {icon && showIcon && buildIcon()}
                <button
                    ref={selectAnchorRef}
                    type="button"
                    className={`${inputClasses} appearance-none text-left pe-7`}
                    aria-haspopup="true"
                    aria-expanded={showSelect}
                    onClick={() => !shouldDisabled && setShowSelect(!showSelect)}
                    disabled={shouldDisabled}
                >
                    <span
                        className={
                            shouldDisabled
                                ? "text-creation-neutral-container-on-surface-disabled"
                                : selectedLabel
                                    ? "text-main-typography-system-default"
                                    : "text-main-typography-system-caption"
                        }
                    >
                        {displayLabel}
                    </span>
                </button>
                {buildChevronIcon({ isExpanded: showSelect, isDisabled: shouldDisabled })}
                {showSelect && !shouldDisabled && (
                    <FloatingPortal
                        anchorRef={selectAnchorRef}
                        isOpen={showSelect}
                        popoverRef={selectPopoverRef}
                        className={`dropdown-list flex flex-col ${dropdownExtraClasses}`}
                        onMouseDown={handlePopoverMouseDown}
                        placement={popoverPlacement}
                    >
                        <div
                            role="listbox"
                            aria-label={placeholder || commonProps.name}
                        >
                            <div
                                ref={menuListContainerRef}
                                className="custom-scrollbar overflow-y-auto dropdown-list-scroll"
                                onScroll={handleSelectScroll}
                            >
                                {choices.map(([val, label, description, disabledOption]) => {
                                    const isSelected = String(value) === String(val);
                                    return (
                                        <div key={val} data-value={val}>
                                            <MenuList
                                                id={val}
                                                label={label}
                                                description={description}
                                                selected={isSelected}
                                                disabled={disabledOption}
                                                onClick={() => !disabledOption && handleSelect(val)}
                                            />
                                        </div>
                                    );
                                })}
                                {isLoadingMoreChoices && (
                                    <div className="flex justify-center py-2 text-sm text-main-typography-system-caption">
                                        กำลังโหลด...
                                    </div>
                                )}
                            </div>
                        </div>
                    </FloatingPortal>
                )}
            </div>
        );
    }

    if (type === "calendar") {
        const calendarContainerRef = useRef(null);
        const calendarAnchorRef = useRef(null);
        const calendarPopoverRef = useRef(null);
        handleClickOutsideElement([calendarContainerRef, calendarPopoverRef], () => setShowCalendarPopover(false));

        const handleDateSubmit = (date) => {
            if (isWeek) {
                const syntheticEvent = {
                    target: {
                        name: commonProps.name,
                        value: date,
                    },
                };
                if (commonProps.onChange) {
                    commonProps.onChange(syntheticEvent);
                }
                setShowCalendarPopover(false);
                return;
            }

            const syntheticEvent = {
                target: {
                    name: commonProps.name,
                    value: date,
                },
            };
            if (commonProps.onChange) {
                commonProps.onChange(syntheticEvent);
            }
            setShowCalendarPopover(false);
        };

        let displayValue = value;
        const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;

        if (value instanceof Date) {
            if (isWeek) {
                displayValue = formatWeekRangeFromDate(value);
            } else {
                displayValue = formatThaiDate(value, { month: 'long', includeYear: true, useChristianYear: calendarEra === "AD", useEnglish: calendarEra === "AD" });
            }
        } else if (value && typeof value === 'string') {
            if (isoDateRegex.test(value)) {
                const date = new Date(value);
                if (!isNaN(date)) {
                    if (isWeek) {
                        displayValue = formatWeekRangeFromDate(date);
                    } else {
                        displayValue = formatThaiDate(date, { month: 'long', includeYear: true, useChristianYear: calendarEra === "AD", useEnglish: calendarEra === "AD" });
                    }
                }
            } else {
                displayValue = value;
            }
        }

        return (
            <div ref={calendarContainerRef} className="relative">
                {showIcon && (
                    <div className="absolute inset-y-0 start-0 flex items-center ms-3 pointer-events-none">
                        <Icon
                            name="calendar"
                            size="md"
                            className={
                                disabled
                                    ? "text-creation-neutral-container-on-surface-disabled"
                                    : "text-creation-neutral-container-on-surface-default"
                            }
                        />
                    </div>
                )}
                <button
                    ref={calendarAnchorRef}
                    type="button"
                    className={`${inputClasses} ${showIcon ? "ps-7" : ""} appearance-none text-left pe-7`}
                    onClick={() => !disabled && setShowCalendarPopover(!showCalendarPopover)}
                    disabled={disabled}
                >
                    <span
                        className={
                            disabled
                                ? "text-creation-neutral-container-on-surface-disabled"
                                : value
                                    ? "text-main-typography-system-default"
                                    : "text-main-typography-system-caption"
                        }
                    >
                        {displayValue || placeholder}
                    </span>
                </button>
                {buildChevronIcon({ isExpanded: showCalendarPopover, isDisabled: disabled })}
                {showCalendarPopover && (
                    <FloatingPortal
                        anchorRef={calendarAnchorRef}
                        isOpen={showCalendarPopover}
                        popoverRef={calendarPopoverRef}
                        matchWidth={false}
                        offset={8}
                        placement={popoverPlacement === "auto" ? "center" : popoverPlacement}
                    >
                        <DatepickerPopover
                            value={value}
                            initialDate={value}
                            onSubmit={handleDateSubmit}
                            minDate={minDate}
                            maxDate={maxDate}
                            allowYearSearch={allowYearSearch}
                            calendarEra={calendarEra}
                        />
                    </FloatingPortal>
                )}
            </div>
        );
    }

    if (type === "search") {
        const selectedTuple = suggestions.find(([v]) => String(v) === String(value)) || searchSuggestions.find(([v]) => String(v) === String(value));
        const selectedLabel = selectedTuple ? selectedTuple[1] : value;
        const shouldShowClearButton =
            allowClear && !disabled && isInputFocused && Boolean(selectedLabel);

        const searchContainerRef = useRef(null);
        const searchAnchorRef = useRef(null);
        const searchPopoverRef = useRef(null);
        const debounceTimeoutRef = useRef(null);
        const searchCurrentPageRef = useRef(1);
        const searchCurrentQueryRef = useRef("");
        handleClickOutsideElement([searchContainerRef, searchPopoverRef], () => setShowSearchSuggestions(false));

        const loadSuggestionsPage = async (query, page) => {
            if (!onSearch) return { results: [], hasMore: false };
            const rawResult = await onSearch(query, { page });
            if (Array.isArray(rawResult)) {
                return { results: rawResult, hasMore: false };
            }
            return {
                results: Array.isArray(rawResult?.results) ? rawResult.results : [],
                hasMore: Boolean(rawResult?.hasMore),
            };
        };

        const handleSearchChange = (e) => {
            const query = e.target.value;
            if (commonProps.onChange) {
                commonProps.onChange(e);
            }

            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }

            debounceTimeoutRef.current = setTimeout(async () => {
                if (query.length < minCharsForSuggestions) {
                    if (!showSuggestionsOnFocus || query.length > 0) {
                        setShowSearchSuggestions(false);
                        setSearchSuggestions([]);
                        setHasMoreSuggestions(false);
                        return;
                    }
                }

                let newSuggestions = [];
                let hasMore = false;
                if (onSearch) {
                    searchCurrentPageRef.current = 1;
                    searchCurrentQueryRef.current = query;
                    ({ results: newSuggestions, hasMore } = await loadSuggestionsPage(query, 1));
                } else if (suggestions.length > 0) {
                    if (!query) {
                        newSuggestions = suggestions;
                    } else {
                        const lowerCaseQuery = query.toLowerCase();
                        newSuggestions = suggestions
                            .filter(([, label]) =>
                                label.toLowerCase().includes(lowerCaseQuery)
                            )
                            .sort(([, a], [, b]) => {
                                const lowerA = a.toLowerCase();
                                const lowerB = b.toLowerCase();
                                const aStartsWith = lowerA.startsWith(lowerCaseQuery);
                                const bStartsWith = lowerB.startsWith(lowerCaseQuery);

                                if (aStartsWith && !bStartsWith) {
                                    return -1;
                                }
                                if (!aStartsWith && bStartsWith) {
                                    return 1;
                                }
                                return lowerA.localeCompare(lowerB);
                            });
                    }
                }

                setSearchSuggestions(newSuggestions);
                setHasMoreSuggestions(hasMore);
                setShowSearchSuggestions(newSuggestions.length > 0 || hasMore);
            }, onSearch ? debounceTimeout : 0);
        };

        const handleFocus = async (event) => {
            if (commonProps.onFocus) {
                commonProps.onFocus(event);
            }
            if (showSuggestionsOnFocus) {
                let newSuggestions = [];
                let hasMore = false;
                if (onSearch) {
                    searchCurrentPageRef.current = 1;
                    searchCurrentQueryRef.current = value || "";
                    ({ results: newSuggestions, hasMore } = await loadSuggestionsPage(value || "", 1));
                } else {
                    newSuggestions = suggestions;
                }
                setSearchSuggestions(newSuggestions);
                setHasMoreSuggestions(hasMore);
                setShowSearchSuggestions(newSuggestions.length > 0 || hasMore);
            } else if (value && searchSuggestions.length > 0) {
                setShowSearchSuggestions(true);
            }
        };

        const handleBlur = (e) => {
            if (commonProps.onBlur) {
                commonProps.onBlur(e);
            }
            if (!allowFreeText) {
                setTimeout(() => {
                    const allSuggestions = [...suggestions, ...searchSuggestions];
                    const isValueInSuggestions = allSuggestions.some(([, label]) => label === e.target.value);
                    if (!isValueInSuggestions && e.target.value !== "") {
                        const syntheticEvent = {
                            target: {
                                name: commonProps.name,
                                value: "",
                                selectedSuggestion: null,
                                selectedSuggestionValue: null,
                            },
                        };
                        if (commonProps.onChange) {
                            commonProps.onChange(syntheticEvent);
                        }
                    }
                }, 100); // Delay to allow suggestion click to process
            }
        };
        const handleClearSearch = () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            emitSyntheticChange("", {
                selectedSuggestion: null,
                selectedSuggestionValue: null,
            });
            setSearchSuggestions([]);
            setShowSearchSuggestions(false);
            setHasMoreSuggestions(false);
            searchCurrentPageRef.current = 1;
            searchCurrentQueryRef.current = "";
            requestAnimationFrame(() => searchAnchorRef.current?.focus());
        };

        const loadMoreSuggestions = async () => {
            if (isLoadingMoreSuggestions || !hasMoreSuggestions || !onSearch) return;
            setIsLoadingMoreSuggestions(true);
            try {
                const nextPage = searchCurrentPageRef.current + 1;
                const { results, hasMore } = await loadSuggestionsPage(searchCurrentQueryRef.current, nextPage);
                searchCurrentPageRef.current = nextPage;
                setSearchSuggestions((prev) => [...prev, ...results]);
                setHasMoreSuggestions(hasMore);
            } finally {
                setIsLoadingMoreSuggestions(false);
            }
        };

        const handleDropdownScroll = (e) => {
            const el = e.currentTarget;
            const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (distanceToBottom < 80 && hasMoreSuggestions && !isLoadingMoreSuggestions) {
                loadMoreSuggestions();
            }
        };

        const handleSuggestionClick = (suggestion) => {
            const [suggestionValue, label] = suggestion;
            const syntheticEvent = {
                target: {
                    name: commonProps.name,
                    value: label,
                    selectedSuggestion: suggestion,
                    selectedSuggestionValue: suggestionValue,
                },
            };
            if (commonProps.onChange) {
                commonProps.onChange(syntheticEvent);
            }
            setShowSearchSuggestions(false);
        };

        const handlePopoverMouseDown = preventPopoverCloseOnClick
            ? (event) => event.stopPropagation()
            : undefined;

        return (
            <div className="relative" ref={searchContainerRef}>
                {showIcon && (
                    <div className="absolute inset-y-0 start-0 flex items-center ms-3 pointer-events-none">
                        <Icon
                            name="search"
                            size="md"
                            className={
                                disabled
                                    ? "text-creation-neutral-container-on-surface-disabled"
                                    : "text-creation-neutral-container-on-surface-default"
                            }
                        />
                    </div>
                )}
                <input
                    ref={searchAnchorRef}
                    {...commonProps}
                    type="search"
                    className={`${inputClasses} ${allowClear ? "pe-7" : ""}`}
                    value={selectedLabel || ""}
                    autoComplete="off"
                    onChange={handleSearchChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
                {shouldShowClearButton &&
                    buildClearButton({
                        onClear: handleClearSearch,
                    })}
                {showSearchSuggestions && (searchSuggestions.length > 0 || hasMoreSuggestions) && (
                    <FloatingPortal
                        anchorRef={searchAnchorRef}
                        isOpen={showSearchSuggestions}
                        popoverRef={searchPopoverRef}
                        className="dropdown-list flex flex-col"
                        onMouseDown={handlePopoverMouseDown}
                        placement={popoverPlacement}
                    >
                        <div
                            className="custom-scrollbar overflow-y-auto dropdown-list-scroll"
                            role="listbox"
                            onScroll={handleDropdownScroll}
                        >
                            {searchSuggestions.map((suggestion, suggestionIndex) => {
                                const [value, label, description, disabledOption] = suggestion;
                                const rawKey =
                                    value && typeof value === "object"
                                        ? value.id ?? suggestionIndex
                                        : value ?? suggestionIndex;
                                const suggestionKey = String(rawKey);
                                return (
                                    <MenuList
                                        key={`${commonProps.name}-${suggestionKey}`}
                                        id={`${commonProps.name}-suggestion-${suggestionKey}`}
                                        label={label}
                                        description={description}
                                        disabled={disabledOption}
                                        onClick={() => !disabledOption && handleSuggestionClick(suggestion)}
                                    />
                                );
                            })}
                            {isLoadingMoreSuggestions && (
                                <div className="flex justify-center py-2 text-sm text-main-typography-system-caption">
                                    กำลังโหลด...
                                </div>
                            )}
                        </div>
                    </FloatingPortal>
                )}
            </div>
        );
    }

    if (type === "date-range") {
        const dateRangeAnchorRef = useRef(null);
        const dateRangePopoverRef = useRef(null);
        handleClickOutsideElement([dateRangeAnchorRef, dateRangePopoverRef], () => setShowDateRangePopover(false));

        const formatRange = (start, end) => {
            if (!start || !end) return "";
            const startStr = formatThaiDate(start, {
                month: "short",
                includeYear: start.getFullYear() !== end.getFullYear(),
            });
            const endStr = formatThaiDate(end, {
                month: "short",
                includeYear: true,
            });
            return `${startStr} - ${endStr}`;
        };

        const displayValueStr = (startDate && endDate) ? formatRange(startDate, endDate) : placeholder;

        return (
            <div ref={dateRangeAnchorRef} className="relative">
                {showIcon && (
                    <div className="absolute inset-y-0 start-0 flex items-center ms-3 pointer-events-none">
                        <Icon
                            name="calendar"
                            size="md"
                            className={
                                disabled
                                    ? "text-creation-neutral-container-on-surface-disabled"
                                    : "text-creation-neutral-container-on-surface-default"
                            }
                        />
                    </div>
                )}
                <button
                    type="button"
                    className={`${inputClasses} ${showIcon ? "ps-7" : ""} appearance-none text-left pe-7 font-normal`}
                    onClick={() => !disabled && setShowDateRangePopover(!showDateRangePopover)}
                    disabled={disabled}
                >
                    <span
                        className={
                            disabled
                                ? "text-creation-neutral-container-on-surface-disabled"
                                : (startDate && endDate)
                                    ? "text-main-typography-system-default"
                                    : "text-main-typography-system-caption"
                        }
                    >
                        {displayValueStr}
                    </span>
                </button>
                {buildChevronIcon({ isExpanded: showDateRangePopover, isDisabled: disabled })}
                {showDateRangePopover && (
                    <FloatingPortal
                        anchorRef={dateRangeAnchorRef}
                        isOpen={showDateRangePopover}
                        popoverRef={dateRangePopoverRef}
                        matchWidth={false}
                        offset={8}
                        placement={popoverPlacement === "auto" ? "center" : popoverPlacement}
                    >
                        <DateRangePickerPopover
                            startDate={startDate}
                            endDate={endDate}
                            onCancel={() => setShowDateRangePopover(false)}
                            onApply={(start, end) => {
                                if (typeof commonProps.onChange === "function") {
                                    commonProps.onChange({
                                        target: {
                                            name: commonProps.name,
                                            value: { startDate: start, endDate: end },
                                            startDate: start,
                                            endDate: end,
                                        },
                                    });
                                }
                                setShowDateRangePopover(false);
                            }}
                        />
                    </FloatingPortal>
                )}
            </div>
        );
    }

    if (type === "password") {
        const toggleType = showPassword ? "text" : "password";

        return (
            <div className="relative">
                <input
                    {...commonProps}
                    ref={inputRef}
                    type={toggleType}
                    className={`${inputClasses} pe-7`}
                    value={value || ""}
                />
                <button
                    type="button"
                    className="absolute inset-y-0 end-0 flex items-center pe-3"
                    tabIndex={-1}
                    onClick={() => setShowPassword(!showPassword)}
                >
                    <Icon
                        name={showPassword ? "eye-off" : "eye"}
                        size="md"
                        className={
                            disabled
                                ? "text-creation-neutral-container-on-surface-disabled"
                                : "text-creation-neutral-container-on-surface-default"
                        }
                    />
                </button>
            </div>
        );
    }

    // Default: text-like input (text, email, etc.)
    const showClearButton =
        allowClear &&
        !disabled &&
        isInputFocused &&
        value !== "" &&
        value !== null &&
        value !== undefined;
    return (
        <div className="relative">
            {icon && showIcon && buildIcon()}
            <input
                {...commonProps}
                ref={inputRef}
                type={inputType || "text"}
                className={`${inputClasses} ${allowClear ? "pe-7" : ""}`}
                value={value || ""}
                autoComplete={commonProps.autoComplete ?? "off"}
            />
            {showClearButton &&
                buildClearButton({
                    onClear: () => emitSyntheticChange(""),
                })}
        </div >
    );
}
